import useStore from "reusable/mobx-store-utils"
import { GapAutocompleteData, GapAutocompleteOptionExtended } from "../types/GapAutocompleteTypes"
import Typography from "@mui/material/Typography"
import TextFieldCustom from "reusable/mui/text-field/TextFieldCustom"
import { AutocompleteRenderInputParams, AutocompleteRenderOptionState, createFilterOptions } from '@mui/material/Autocomplete'
import Box from "@mui/material/Box"
import Check from '@mui/icons-material/Check'
import { isArray } from "reusable/utils/array-utils"
import PlusIcon from '@mui/icons-material/Add'

import { FilterOptionsState } from '@mui/material/useAutocomplete'
import { AutocompleteOption } from "reusable/mui/auto-complete/shared/AutocompleteOption"
import { SxProps } from "@mui/material/styles"
import { themeConfig } from '@gap/shared/theme'

export const gapAutoCompleteRenderInput = (props: { label: string, variant?: 'standard' | 'outlined' | 'filled', helperText?: string, error?: boolean, readOnly?: boolean | undefined }) => (params: AutocompleteRenderInputParams) => {
   const { label, helperText, error, readOnly } = props
   params.inputProps.readOnly = readOnly

   const variant = props.variant || 'outlined'

   return <TextFieldCustom
      {...params}
      helperText={helperText}
      error={error}
      label={label}
      variant={variant}
   />
}

/**
 * Splits `label` on (case-insensitive) occurrences of `query` and bolds the matching
 * segments. Returns the original text untouched when `query` is empty.
 */
const renderHighlightedLabel = (label: string, query: string) => {
   if (!query) return label
   const lowerLabel = label.toLowerCase()
   const lowerQuery = query.toLowerCase()
   const parts: React.ReactNode[] = []
   let cursor = 0
   while (cursor < label.length) {
      const matchAt = lowerLabel.indexOf(lowerQuery, cursor)
      if (matchAt === -1) {
         parts.push(label.slice(cursor))
         break
      }
      if (matchAt > cursor) parts.push(label.slice(cursor, matchAt))
      parts.push(<strong key={matchAt}>{label.slice(matchAt, matchAt + query.length)}</strong>)
      cursor = matchAt + query.length
   }
   // Wrap in a single span so the surrounding flex container treats the highlighted
   // label as one inline item — otherwise a text segment starting with whitespace
   // (e.g. " Heijn") becomes its own flex item and the leading space is collapsed.
   return <span>{parts}</span>
}

export const gapAutoCompleteRenderOption = (displaySelectedIcon: boolean, sectionDividerColour: string) =>
   (props: React.HTMLAttributes<HTMLLIElement>, option: GapAutocompleteOptionExtended, { selected, inputValue }: AutocompleteRenderOptionState) => {
      const sx: SxProps<AutocompleteOption> = {
         '&[data-gap-option] + &[data-gap-option--heading]': {
            borderTop: `1px solid ${sectionDividerColour}`
         }
      }
      const containerSx: SxProps<AutocompleteOption> = {
         '&[data-gap-option]': {
            flexDirection: 'column',
            alignItems: 'start',
            justifyContent: 'center',
            paddingY: 1,
         }
      }

      if (option.specialOption) {
         return (
            <Box component='li' data-gap-option--special data-option-type="special" {...{ sx }} {...props}>
               <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <PlusIcon sx={{ fontSize: '24px' }} />
                  <span style={{ lineHeight: '24px' }}>
                     Create “<strong>{option.inputValue}</strong>”
                  </span>
               </Box>
            </Box>
         )
      }

      if (!option.isSelectable) {
         return <Box component="li" data-gap-option--heading data-option-type={option.type === 'category' ? 'category' : 'subcategory'} data-id={`${option.type}-${option.id}`} key={`${option.type}-${option.id}`}  {...{ sx }}>
            <GapAutocompleteCategoryOrSubcategory {...option} />
         </Box>
      }

      return (
         <Box component="li" {...{ sx: { ...sx, ...containerSx } }} {...props} data-gap-option data-option-type={option.sublabel ? 'option-with-sublabel' : 'option'} data-id={option.id} key={option.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
               <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  {option.avatar && (
                     <Box sx={{ mr: 2 }}>
                        {option.avatar}
                     </Box>
                  )}
                  {renderHighlightedLabel(option.label, inputValue)}
               </Box>


               {selected && displaySelectedIcon && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', minWidth: 'fit-content' }}>
                     <Check fontSize='small' color="primary" />
                  </Box>
               )}
            </Box>

            {option.sublabel && <Typography variant="body2" sx={{ color: themeConfig.primary['600'] }}>{option.sublabel}</Typography>}
         </Box>
      )
   }

export const gapAutocompleteIsOptionEqualToValue = (option: GapAutocompleteOptionExtended, value: GapAutocompleteOptionExtended) => option.id === value.id

/**
 * If hierarchical data is provided, this flattens it into a single list of options and sets
 * the category and subcategory as non-selectable options. If the data is already flat, it
 * returns the same list of options.
 * @returns Flattened list of options
 */
export const gapAutocompleteNormaliseOptions = (options: GapAutocompleteData): GapAutocompleteOptionExtended[] => {
   const flatData: GapAutocompleteOptionExtended[] = []

   // If the data is hierarchical i.e. GapAutocompleteHierarchy
   if (!isArray(options)) {
      options.items?.forEach(item => flatData.push({
         ...item,
         isSelectable: true,
         type: 'option',
      }))

      options.categories.forEach(option => {

         // Add category as non-selectable and type it appropriately
         flatData.push({
            id: option.id,
            label: option.label,
            isSelectable: false,
            type: 'category',
         })

         option.subcategories?.forEach(subcategory => {
            // Add subCategory as non-selectable and type it
            flatData.push({
               id: subcategory.id,
               label: subcategory.label,
               isSelectable: false,
               type: 'subcategory',
            })

            // Add subCategory's items
            subcategory.items.forEach(item => flatData.push({
               ...item,
               isSelectable: true,
               type: 'option',
            }))
         })

         // Add category's direct items, if any
         option.items?.forEach(item => flatData.push({
            ...item,
            isSelectable: true,
            type: 'option',
         }))
      })
   }
   else {
      options.forEach(option => {
         flatData.push({
            ...(option),
            isSelectable: true,
            type: 'option',
         })
      })
   }
   return flatData
}

export const gapAutocompleteFilterOptions = (
   options: GapAutocompleteOptionExtended[],
   params: FilterOptionsState<GapAutocompleteOptionExtended>
): GapAutocompleteOptionExtended[] => {
   const filter = createFilterOptions<GapAutocompleteOptionExtended>()
   const filtered = filter(options, params)

   const { inputValue } = params

   const isExisting = options.some(option => option.label.toLowerCase() === inputValue.toLowerCase())
   if (inputValue !== '' && !isExisting) {
      filtered.push({
         id: `add-${inputValue}`,
         label: `+ Create "${inputValue}"`,
         specialOption: true,
         inputValue,
         isSelectable: true,
         type: 'option',
      })
   }

   return filtered
}

/**
 * Category-aware filter for hierarchical options. Filters leaf items by the input
 * and preserves any category/subcategory header that still has a matching descendant.
 */
export const gapAutocompleteHierarchyFilterOptions = (
   options: GapAutocompleteOptionExtended[],
   params: FilterOptionsState<GapAutocompleteOptionExtended>
): GapAutocompleteOptionExtended[] => {
   if (!params.inputValue) return options

   const filter = createFilterOptions<GapAutocompleteOptionExtended>()
   const matchedLeafIds = new Set(filter(options.filter(o => o.isSelectable), params).map(o => o.id))

   const result: GapAutocompleteOptionExtended[] = []
   for (let i = 0; i < options.length; i++) {
      const option = options[i]
      if (option.isSelectable) {
         if (matchedLeafIds.has(option.id)) result.push(option)
         continue
      }
      // Header: include only if at least one descendant leaf survived the filter.
      const headerLevel = option.type === 'category' ? 0 : 1
      let hasMatchingDescendant = false
      for (let j = i + 1; j < options.length; j++) {
         const next = options[j]
         if (!next.isSelectable) {
            const nextLevel = next.type === 'category' ? 0 : 1
            if (nextLevel <= headerLevel) break
            continue
         }
         if (matchedLeafIds.has(next.id)) {
            hasMatchingDescendant = true
            break
         }
      }
      if (hasMatchingDescendant) result.push(option)
   }
   return result
}

export const gapAutocompleteGetOptionLabel = (option: string | GapAutocompleteOptionExtended): string => {
   if (typeof option === 'string') {
      return option
   }

   if (option.inputValue) {
      return option.inputValue
   }

   return option.label
}

export const GapAutocompleteCategoryOrSubcategory = (props: GapAutocompleteOptionExtended) => {
   const localStore = useStore(() => ({
      handleClick: (e: React.MouseEvent<HTMLDivElement>) => {
         e.stopPropagation()
      }
   }))

   return <Typography
      id={props.id}
      component="div"
      onClick={localStore.handleClick}
      {...props.type === 'category' ? {
         variant: 'subtitle2',
         sx: {
            fontWeight: 400,
            p: 2,
            cursor: 'default',
         },
      } : {
         variant: 'body2',
         sx: {
            fontWeight: 'bold',
            pl: 2,
            pt: 1,
            pb: 2,
            cursor: 'default',
         },
      }}
   >
      {props.label}
   </Typography>
}
